import { useEffect, useState } from 'react';
import PageHeader from '../../components/owner/PageHeader';
import { defaultProfile } from './profileData';
import { setReceptionistBranch } from '../../services/gym/branchContext';
import {
  fetchCurrentReceptionistProfile,
  updateReceptionistProfile,
} from '../../services/receptionist/receptionistApi';
import { usePanelToast } from '../../context/PanelToastContext';
import '../owner/ownerPages.css';

function Profile() {
  const toast = usePanelToast();
  const [profile, setProfile] = useState({ ...defaultProfile });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchCurrentReceptionistProfile();
        if (cancelled) return;

        if (data) {
          setProfile({
            name: data.name,
            email: data.email,
            phone: data.phone,
            branch: data.branch,
            shift: data.shift,
          });
          if (data.branch) setReceptionistBranch(data.branch);
        } else {
          setError('No receptionist profile found. Ask your administrator to add you in Receptionist Management.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateProfile = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const updated = await updateReceptionistProfile({
        name: profile.name,
        phone: profile.phone,
      });
      setProfile({
        name: updated.name,
        email: updated.email,
        phone: updated.phone,
        branch: updated.branch,
        shift: updated.shift,
      });
      if (updated.branch) setReceptionistBranch(updated.branch);
      toast.showSaved('Profile');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Profile" subtitle="Manage your account" variant="banner">
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading profile...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card owner-mb-28 rec-panel-card">
        <h2 className="owner-card-title">Update Profile</h2>

        <div className="rec-sales-form-grid">
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="full-name">Full Name</label>
            <input
              id="full-name"
              className="owner-form-input"
              value={profile.name}
              onChange={(e) => updateProfile('name', e.target.value)}
              disabled={loading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="owner-form-input"
              value={profile.email}
              readOnly
              aria-readonly="true"
              title="Email cannot be changed"
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              className="owner-form-input"
              value={profile.phone}
              onChange={(e) => updateProfile('phone', e.target.value)}
              disabled={loading || saving}
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="branch">Branch</label>
            <input
              id="branch"
              className="owner-form-input"
              value={profile.branch || 'Not assigned'}
              readOnly
              aria-readonly="true"
              title="Branch is assigned by your administrator"
            />
          </div>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="shift">Shift</label>
            <input id="shift" className="owner-form-input" value={profile.shift} readOnly />
          </div>
        </div>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={handleSave}
          disabled={loading || saving || !profile.name.trim()}
        >
          {saving ? 'Saving...' : 'Update Profile'}
        </button>
      </div>
    </div>
  );
}

export default Profile;
